import { Box, Button, Tooltip, Typography } from "@mui/material";
import InfoPopper from "../../../components/popper/InfoPopper";
import { ChangeEvent, MouseEventHandler, useEffect, useState } from "react";
import { FileRejection } from "react-dropzone";

import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";

import { ActivitiesRef, ErrorActivitiesRef } from "../ImportFilePopup.type";
import { useTranslation } from "react-i18next";
import { common, input, organization } from "../../../i18n";
import InfoCircleIcon from "../../../components/icon/InfoCircle";
import {
  MAX_UPLOAD_FILE_SIZE,
  MAX_UPLOAD_FILE_SIZE_IMPORT,
} from "../../../constants/form";
import alertService from "../../../components/alert/alertService";
import FileUtils from "../../../utils/fileUtils";
import ActivitiesService from "../../../service/activities.service";
import { ApiError } from "../../../api/ApiError";
import dayjs from "dayjs";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  tableStyle,
  VisuallyHiddenInput,
} from "../../emission/_components/data-table/EmissionDataTableStyle";

interface Props {
  onClose: MouseEventHandler<HTMLButtonElement> | undefined;
  activitiesRef: ActivitiesRef[];
  fileErrors: FileRejection[];
  setImportResult: (importResult: ActivitiesRef[]) => void;
}

export const ImportDataStepThree = ({
  onClose,
  fileErrors,
  activitiesRef,
  setImportResult,
}: Props): JSX.Element => {
  const { t, i18n } = useTranslation([common, input, organization], {
    nsMode: "fallback",
  });

  const [groupedRows, setGroupedRows] = useState<ErrorActivitiesRef[]>([]);

  const [rowCount, setRowCount] = useState<number>(0);

  const uploadFile = (
    event: ChangeEvent<HTMLInputElement>,
    row: ErrorActivitiesRef,
  ) => {
    const file: FileList | null = event.currentTarget.files;

    if (file && file[0]) {
      if (file[0].size > MAX_UPLOAD_FILE_SIZE) {
        alertService.info(
          t("Maximum-file-size-exceeded", {
            size: (MAX_UPLOAD_FILE_SIZE / 1e6).toFixed(0),
          }),
        );

        event.target.value = "";
      } else {
        FileUtils.getBase64(file[0])
          .then(async (res) => {
            const startDate = dayjs(row.activitiesRef?.startDate);

            let result;
            try {
              result = await ActivitiesService.updateAttachment(
                row.activitiesRef!._id,
                res as string,
                startDate.year(),
              );
            } catch {
              // error alert already shown by service
              return;
            }
            if (result) {
              alertService.mixin(t("Upload-Evidence-Success"));

              const index: number = groupedRows.findIndex(
                (g) => g.activitiesRef?._id === row.activitiesRef!._id,
              );

              if (index >= 0) {
                let temp: ErrorActivitiesRef[] = [...groupedRows];

                let tempItem: ErrorActivitiesRef = groupedRows[index];

                tempItem.activitiesRef!.attachmentRef = {
                  _id: "",
                  originalfileName: file[0].name,
                };

                const activitiesRefTemp: ActivitiesRef[] = [...activitiesRef];

                const activitiesRefIndex = activitiesRefTemp.findIndex(
                  (a) => a._id === tempItem.activitiesRef?._id,
                );

                activitiesRefTemp[activitiesRefIndex] = tempItem.activitiesRef!;

                setImportResult([...activitiesRefTemp]);
              }
            }
          })
          .catch((err) => {
            if (err instanceof ApiError) return;
            console.log(err);
            event.target.value = "";
            alertService.error(t("Invalid-File-Type"));
          });
      }
    }
  };

  const columns: GridColDef<ErrorActivitiesRef>[] = [
    {
      field: "id",
      headerName: t("ID"),
      disableColumnMenu: true,
      type: "string",
      headerAlign: "left",
      align: "left",

      valueFormatter: (value, row) => `EM-${row.id}`,
      colSpan: (value, row) => {
        if (row.isGroupHeader === true) {
          return 6;
        }
        return undefined;
      },
      renderCell: (params) => {
        return params.row.isGroupHeader ? (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            bgcolor={"var(--gray3)"}
            px={1}
            width={"100%"}
            height={"100%"}
          >
            {params.row.id === "Successfully" ? (
              <>
                <CheckCircleOutlineIcon color="primary" />
                <Typography variant="text1">
                  {`${t("Successfully")}`}{" "}
                </Typography>
              </>
            ) : (
              <>
                <InfoCircleIcon color="error" />
                <Typography variant="text1">
                  {`${t("Upload-Incomplete")} / ${
                    params.row.id === "Filename-Mismatch"
                      ? t("Filename-Mismatch")
                      : params.row.id === "file-too-large"
                      ? t("maximum-file-size", {
                          size: (MAX_UPLOAD_FILE_SIZE_IMPORT / 1e6).toFixed(0),
                        })
                      : params.row.id
                  }`}
                </Typography>
              </>
            )}
          </Box>
        ) : undefined;
      },
    },
    {
      field: "siteRef",
      headerName: t("Site-Name"),
      headerAlign: "left",
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
      valueFormatter: (value, row) => row.activitiesRef!.siteRef.name,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "assetRef",
      headerName: t("Asset"),
      headerAlign: "left",
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
      valueFormatter: (value, row) => row.activitiesRef!.assetRef.name,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "emissionFactorRef",
      headerName: t("Emission-Source"),
      headerAlign: "left",
      disableColumnMenu: true,
      minWidth: 200,
      valueFormatter: (value, row) =>
        i18n.language.toLowerCase().includes("en")
          ? row.activitiesRef!.emissionFactorRef.attributeEn
          : row.activitiesRef!.emissionFactorRef.attributeTh,
      flex: 1,
    },
    // {
    //   field: "error",
    //   headerName: "error",
    //   headerAlign: "left",
    //   disableColumnMenu: true,
    //   minWidth: 200,
    //   flex: 1,
    //   valueFormatter: (value, row) => {
    //     console.log(console.log(row.id), row.activitiesRef);
    //   },
    // },
    {
      field: "attachmentRef",
      headerName: t("File-Name"),
      sortable: false,
      headerAlign: "left",
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
      minWidth: 180,
      flex: 1,
      valueFormatter: (value, row) =>
        row.activitiesRef!.attachmentRef?.originalfileName ?? "",
      renderCell: (params: GridRenderCellParams<ErrorActivitiesRef>) => {
        return (
          <Tooltip title={params.formattedValue}>
            <Box
              onClick={
                params.formattedValue !== ""
                  ? async () => {
                      try {
                        const file = await ActivitiesService.getAttachmentMeta(
                          params.row.activitiesRef!._id,
                        );

                        if (file) {
                          const blobFile = FileUtils.base64ToFile(
                            file.attachment,
                          );

                          if (!FileUtils.canOpenInNewTab(blobFile.type)) {
                            alertService.info(t("Cannot-Preview-File"));
                            return;
                          }

                          window.open(URL.createObjectURL(blobFile));
                        }
                      } catch (error) {
                        alertService.info(t("Cannot-Preview-File"));
                      }
                    }
                  : undefined
              }
            >
              <Typography
                variant="text1"
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {params.formattedValue}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "",
      headerName: "",
      sortable: false,
      headerAlign: "left",
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
      width: 170,
      renderCell: (params: GridRenderCellParams<ErrorActivitiesRef>) => {
        return (
          <Button
            key={JSON.stringify(params.row)}
            component="label"
            role={undefined}
            variant="text"
            tabIndex={-1}
            color={"secondary"}
            sx={{
              textDecoration: "underline",
            }}
          >
            {params.row.activitiesRef?.attachmentRef?.originalfileName
              ? t("Edit")
              : t("Add-New-File")}

            <VisuallyHiddenInput
              type="file"
              onChange={async (event) => uploadFile(event, params.row)}
              size={MAX_UPLOAD_FILE_SIZE_IMPORT}
              accept="image/*,application/pdf,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            />
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    const groupedRowsTemp: ErrorActivitiesRef[] = [];

    const rows: ActivitiesRef[] = activitiesRef.filter((a) => !a.attachmentRef);

    const fileIds = new Set(fileErrors.map((f) => f.errors[0].code));

    const filenameMismatch = [...rows.filter((e) => !e.error)];

    if (filenameMismatch.length > 0) {
      groupedRowsTemp.push({
        id: "Filename-Mismatch",
        isGroupHeader: true,
        error: "Filename-Mismatch",
      });

      groupedRowsTemp.push(
        ...filenameMismatch.map(
          (e): ErrorActivitiesRef => ({
            id: `${e.id}`,
            isGroupHeader: false,
            activitiesRef: e,
            error: "Filename-Mismatch",
          }),
        ),
      );
    }

    fileIds.forEach((f) => {
      const temp = [
        ...rows.filter((e) => e.error && e.error.errors[0].code === f),
      ];

      if (temp.length > 0) {
        groupedRowsTemp.push({
          id: `${f}`,
          isGroupHeader: true,
          error: `${f}`,
        });

        groupedRowsTemp.push(
          ...temp.map(
            (e): ErrorActivitiesRef => ({
              id: `${e.id}`,
              isGroupHeader: false,
              activitiesRef: e,
              error: `${f}`,
            }),
          ),
        );
      }
    });

    //Successfully

    const successfullyList: ActivitiesRef[] = [
      ...activitiesRef.filter((a) => a.attachmentRef),
    ];

    if (successfullyList.length > 0) {
      groupedRowsTemp.push({
        id: `Successfully`,
        isGroupHeader: true,
        error: ``,
      });

      groupedRowsTemp.push(
        ...activitiesRef
          .filter((a) => a.attachmentRef)
          .map(
            (e): ErrorActivitiesRef => ({
              id: `${e.id}`,
              isGroupHeader: false,
              activitiesRef: e,
              error: ``,
            }),
          ),
      );
    }

    setGroupedRows(groupedRowsTemp);

    setRowCount(
      groupedRowsTemp.filter((f) => f.isGroupHeader === false).length,
    );
  }, [activitiesRef]);

  return (
    <>
      <Typography
        variant="h3"
        color={"var(--dark)"}
        sx={{
          marginBottom: "8px",
          borderBlockStart: "1px",
        }}
      >
        {`${t("Evidence-File")}`}

        <InfoPopper text={t("Attachment-Description")} />
      </Typography>

      {rowCount > 0 && (
        <Box height={"320px"}>
          <AppDataGrid
            rows={groupedRows}
            columns={columns}
            rowHeight={60}
            sx={{
              ...tableStyle(),
              "& .with-out-padding": {
                paddingLeft: "0 !important",
                paddingRight: "0 !important",
              },
            }}
            getRowHeight={(params) =>
              params.model.isGroupHeader ? 48 : undefined
            }
            rowCount={rowCount}
            getRowClassName={(params) =>
              params.row.isGroupHeader ? "with-out-padding" : ""
            }
          />
        </Box>
      )}

      <Button
        variant="contained"
        color="secondary"
        type="button"
        fullWidth
        onClick={onClose}
      >
        {t("Done")}
      </Button>
    </>
  );
};
